// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { Appointment } from '@medplum/fhirtypes';

/**
 * Formats an ISO datetime string as a human-readable date.
 * @param isoString - ISO datetime string
 * @returns Formatted date (e.g., "February 20, 2026")
 */
export function formatAppointmentDate(isoString: string | undefined): string {
  if (!isoString) {
    return '—';
  }
  return new Date(isoString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Formats start/end ISO datetimes as a time range.
 * @param start - ISO datetime string for start
 * @param end - ISO datetime string for end
 * @param options - Optional. `includeTimezone` appends the browser's short
 *   timezone name (e.g., "EDT") to the range.
 * @returns Formatted time range (e.g., "5:15PM–5:30PM" or "5:15PM–5:30PM EDT")
 */
export function formatAppointmentTime(
  start: string | undefined,
  end: string | undefined,
  options?: { includeTimezone?: boolean }
): string {
  if (!start || !end) {
    return '—';
  }

  const formatTime = (iso: string): string => {
    return new Date(iso)
      .toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
      .replace(' ', '');
  };

  const range = `${formatTime(start)}–${formatTime(end)}`;
  if (!options?.includeTimezone) {
    return range;
  }

  const tz = getTimezoneAbbreviation(start);
  return tz ? `${range} ${tz}` : range;
}

function getTimezoneAbbreviation(iso: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      timeZoneName: 'short',
    }).formatToParts(new Date(iso));
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

/**
 * Formats a numeric copay amount as currency.
 * @param amount - Copay amount in dollars
 * @returns Formatted currency string (e.g., "$80.00")
 */
export function formatCopay(amount: number | undefined): string {
  if (amount === undefined || amount === null) {
    return '$0.00';
  }
  return `$${amount.toFixed(2)}`;
}

/**
 * Extracts the provider (Practitioner) info from an Appointment resource.
 * @param appointment - FHIR Appointment resource
 * @returns Provider name and ID, or defaults
 */
export function getProviderFromAppointment(appointment: Appointment): { name: string; id: string } {
  const practitionerParticipant = appointment.participant?.find((p) => p.actor?.reference?.startsWith('Practitioner/'));

  if (!practitionerParticipant?.actor) {
    return { name: 'Provider', id: '' };
  }

  return {
    name: practitionerParticipant.actor.display ?? 'Provider',
    id: practitionerParticipant.actor.reference?.replace('Practitioner/', '') ?? '',
  };
}

/**
 * Extracts the display name for an appointment's type.
 * @param appointment - FHIR Appointment resource
 * @returns Display string from the first appointmentType coding, or 'Appointment'
 */
export function getAppointmentTypeDisplay(appointment: Appointment): string {
  const coding = appointment.appointmentType?.coding?.[0];
  return coding?.display ?? coding?.code ?? 'Appointment';
}
